import { useQuery } from "@tanstack/react-query";
import React from "react";
import { getDashboardRecentActivity } from "../../../Actions/DashboardActions";
import { useNavigate } from "react-router-dom";

const RecentActivity = ({ user }) => {
  const navigate = useNavigate();
  
  const { data: activities, isLoading } = useQuery({
    queryFn: () => getDashboardRecentActivity(user?.organizationId, 12),
    queryKey: ["dashboardRecentActivity", user?.organizationId],
    enabled: !!user?.organizationId,
    staleTime: 30000,
    cacheTime: 60000,
  });

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "survey_created":
        return (
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </div>
        );
      case "complete":
        return (
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        );
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      live: "bg-green-100 text-green-700",
      paused: "bg-yellow-100 text-yellow-700",
      completed: "bg-blue-100 text-blue-700",
      draft: "bg-gray-100 text-gray-700",
    };
    return colors[status] || colors.draft;
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-[#E0E0E0] rounded-xl overflow-hidden h-[420px]">
        <div className="px-6 py-4 border-b border-[#E0E0E0] bg-gray-50/50">
          <div className="h-5 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
        <div className="p-4 space-y-3">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-gray-200"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                <div className="h-3 bg-gray-100 rounded w-1/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E0E0E0] rounded-xl overflow-hidden h-[420px] flex flex-col">
      <div className="px-5 py-4 border-b border-[#E0E0E0] bg-gray-50/50 flex items-center justify-between">
        <div>
          <h3 className="text-[#0226BE] text-[15px] font-[500]">
            Recent Activity
          </h3>
          <p className="text-[11px] text-[#666] mt-0.5">
            Latest updates from your account
          </p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {activities?.length > 0 ? (
          <div className="divide-y divide-[#E0E0E0]">
            {activities.map((activity, index) => (
              <div
                key={index}
                className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => {
                  if (activity.surveyNumber) {
                    navigate(`/surveys/${activity.surveyNumber}/details`);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  {getActivityIcon(activity.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#333] font-medium truncate">
                      {activity.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-[#999]">
                        {formatTime(activity.timestamp)}
                      </span>
                      {activity.status && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(
                            activity.status
                          )}`}
                        >
                          {activity.status}
                        </span>
                      )}
                      {activity.cpi && (
                        <span className="text-xs text-green-600 font-medium">
                          +${activity.cpi.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-[#666] text-sm">
            No recent activity
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentActivity;
